import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Mail, ArrowRight, Loader2, KeyRound, ArrowLeft } from 'lucide-react';

const CustomerLogin = () => {
  const navigate = useNavigate();
  // Three flows: email→code (existing), email+password (new), and a request
  // for a password-setup link if the customer has never set one before.
  const [mode, setMode] = useState('password'); // 'password' | 'code'
  const [step, setStep] = useState('email');     // 'email' | 'code' (code-mode only)
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Email and password required');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post('/customer/login-password', { email, password });
      localStorage.setItem('customerToken', data.token);
      localStorage.setItem('customerEmail', data.customer.email);
      localStorage.setItem('customerName', data.customer.name);
      toast.success(`Welcome back, ${data.customer.name}!`);
      navigate('/portal');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSetup = async () => {
    if (!email) {
      toast.error('Enter your email first');
      return;
    }
    setLoading(true);
    try {
      await axios.post('/customer/request-password-setup', { email });
      toast.success('Check your inbox for a setup link.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send setup email');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/customer/login', { email });
      toast.success('Login code sent to your email!');
      setStep('code');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/customer/verify', { email, code });
      localStorage.setItem('customerToken', response.data.token);
      localStorage.setItem('customerEmail', response.data.customer.email);
      localStorage.setItem('customerName', response.data.customer.name);
      toast.success('Welcome back!');
      navigate('/portal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '40px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{
            width: '70px',
            height: '70px',
            background: 'linear-gradient(135deg, #e74c3c, #c0392b)',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 8px 20px rgba(231, 76, 60, 0.3)'
          }}>
            <span style={{ fontSize: '32px', color: 'white', fontWeight: 'bold' }}>S</span>
          </div>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#1a1a2e',
            margin: '0 0 8px'
          }}>
            Customer Portal
          </h1>
          <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>
            Track your orders & view proposals
          </p>
        </div>

        {/* Mode toggle — password (default) vs one-time email code (legacy/fallback) */}
        <div style={{ display: 'flex', gap: 8, background: '#f1f5f9', padding: 4, borderRadius: 999, marginBottom: 24 }}>
          {[{ id: 'password', label: 'Password' }, { id: 'code', label: 'Email code' }].map((opt) => (
            <button
              key={opt.id}
              type="button"
              data-testid={`mode-tab-${opt.id}`}
              onClick={() => { setMode(opt.id); setStep('email'); }}
              style={{
                flex: 1, padding: '8px 12px', borderRadius: 999, border: 'none',
                background: mode === opt.id ? '#fff' : 'transparent',
                color: mode === opt.id ? '#0f172a' : '#64748b',
                fontWeight: 600, fontSize: 13, cursor: 'pointer',
                boxShadow: mode === opt.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {mode === 'password' ? (
          <form onSubmit={handlePasswordLogin} data-testid="password-login-form">
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#374151', fontSize: 14 }}>Email Address</label>
              <div style={{ position: 'relative' }}>
                <Mail size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ paddingLeft: 44, height: 48, fontSize: 16 }}
                  data-testid="password-email-input"
                />
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#374151', fontSize: 14 }}>Password</label>
              <div style={{ position: 'relative' }}>
                <KeyRound size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <Input
                  type="password"
                  placeholder="Your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ paddingLeft: 44, height: 48, fontSize: 16 }}
                  data-testid="password-input"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={handleRequestSetup}
              data-testid="forgot-password-link"
              style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 16 }}
            >
              Forgot / haven&apos;t set a password? Email me a setup link →
            </button>
            <Button
              type="submit"
              disabled={loading}
              data-testid="password-login-button"
              style={{ width: '100%', height: 48, background: 'linear-gradient(135deg, #e74c3c, #c0392b)', fontSize: 16, fontWeight: 600 }}
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : (<>Sign In <ArrowRight size={18} style={{ marginLeft: 8 }} /></>)}
            </Button>
          </form>
        ) : step === 'email' ? (
          <form onSubmit={handleSendCode}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#374151',
                fontSize: '14px'
              }}>
                Email Address
              </label>
              <div style={{ position: 'relative' }}>
                <Mail size={18} style={{ 
                  position: 'absolute', 
                  left: '14px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: '#9ca3af'
                }} />
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ 
                    paddingLeft: '44px',
                    height: '48px',
                    fontSize: '16px'
                  }}
                  data-testid="customer-email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                height: '48px',
                background: 'linear-gradient(135deg, #e74c3c, #c0392b)',
                fontSize: '16px',
                fontWeight: '600'
              }}
              data-testid="send-code-button"
            >
              {loading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <>
                  Send Login Code
                  <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                </>
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyCode}>
            <button
              type="button"
              onClick={() => setStep('email')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#6b7280',
                fontSize: '14px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                marginBottom: '20px',
                padding: 0
              }}
            >
              <ArrowLeft size={16} />
              Back
            </button>

            <div style={{
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '24px'
            }}>
              <p style={{ margin: 0, color: '#166534', fontSize: '14px' }}>
                We sent a code to <strong>{email}</strong>
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#374151',
                fontSize: '14px'
              }}>
                Enter 6-Digit Code
              </label>
              <div style={{ position: 'relative' }}>
                <KeyRound size={18} style={{ 
                  position: 'absolute', 
                  left: '14px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: '#9ca3af'
                }} />
                <Input
                  type="text"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={{ 
                    paddingLeft: '44px',
                    height: '48px',
                    fontSize: '24px',
                    letterSpacing: '8px',
                    textAlign: 'center',
                    fontWeight: '600'
                  }}
                  maxLength={6}
                  data-testid="customer-code-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || code.length !== 6}
              style={{
                width: '100%',
                height: '48px',
                background: code.length === 6 
                  ? 'linear-gradient(135deg, #e74c3c, #c0392b)'
                  : '#d1d5db',
                fontSize: '16px',
                fontWeight: '600'
              }}
              data-testid="verify-code-button"
            >
              {loading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                'Verify & Login'
              )}
            </Button>

            <button
              type="button"
              onClick={handleSendCode}
              disabled={loading}
              style={{
                width: '100%',
                marginTop: '16px',
                color: '#6b7280',
                fontSize: '14px',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Didn't receive code? <span style={{ color: '#e74c3c' }}>Resend</span>
            </button>
          </form>
        )}

        {/* Admin link */}
        <div style={{ 
          marginTop: '30px', 
          paddingTop: '20px', 
          borderTop: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>
            Staff member?{' '}
            <a 
              href="/login" 
              style={{ color: '#e74c3c', textDecoration: 'none', fontWeight: '500' }}
            >
              Admin Login
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;
